import type { QuestIntent } from "@-/dev.improve.haniverse.lib.quest.grade";

/**
 * Where a quest is offered. A city quest belongs to one city of the map and is only played there.
 * A global quest is offered wherever the learner is, before their first journey and on the road
 * included; the introductory quests are global.
 */
export type QuestScope = { adcode: number; kind: "city" } | { kind: "global" };

/** What the learner must have learned before a quest unlocks. */
export type QuestRequirement =
  | { kind: "none" }
  /**
   * Every lesson of one pronunciation level, counted as Browse → Levels counts them: P2 (`2-P2`)
   * is `pronunciationIndex: 1`. The traditional curriculum has no pronunciation levels, so there
   * the quest unlocks once every character it asks for is studied.
   */
  | { kind: "pronunciation-level"; pronunciationIndex: number };

/** Mandarin in either script, with its reading. */
export interface QuestMandarin {
  /** Tone-number pinyin, spaced between words (`ni3 xi3huan0 na3ge0`). */
  pinyin: string;
  simplified: string;
  traditional: string;
}

/** Mandarin with its meaning. */
export interface QuestPhrase extends QuestMandarin {
  english: string;
}

/** The pictures Han can hold up to his camera (`quest-scene.tsx` displays them). */
export const QUEST_SCENE_IDS = [
  "calendar",
  "calendars",
  "calendar-styles",
  "dirty-calendar",
] as const;

/** A picture drawn into the conversation, which the next reply is about. */
export type QuestSceneId = (typeof QUEST_SCENE_IDS)[number];

/** Han's caption, with any Mandarin between his introduction and follow-up question. */
export interface QuestHanLine {
  afterMandarin?: { markdown?: string; text: string };
  kind: "han";
  mandarin?: QuestPhrase;
  markdown?: string;
  text: string;
}

export type QuestStep =
  | QuestHanLine
  | { kind: "narration"; markdown?: string; text: string }
  | { kind: "scene"; scene: QuestSceneId }
  /**
   * The learner answers out loud. It counts only when the reply is all Mandarin and Han would
   * have understood it as one of `intents`. `answer` is what to say, shown after a miss; `hint`
   * is a line Han says when the learner asks for help before replying.
   */
  | { answer: QuestPhrase; hint?: QuestHanLine; intents: readonly QuestIntent[]; kind: "reply" };

export interface Quest {
  /** Han paid once to unlock it (what its errand costs). */
  costHan: number;
  /** Stable: the learner's `user_quest` row is keyed by it. */
  id: string;
  requirement: QuestRequirement;
  scope: QuestScope;
  /** The conversation, in order. */
  steps: readonly QuestStep[];
  /** One line under the title in the quest list. */
  summary: string;
  title: string;
  /** Paid once, on the first completion. */
  xp: number;
}
